<script>
  import { RolesRepository, GroupsRepository } from "../../repositories/index";
  import { onMount, getContext } from "svelte";
  import { _, callout } from "../../stores/index";
  import { ActionFooter, Button } from "@gl/design-system";
  import { PoliciesTable, CustomAutocomplete } from "../../components/index";
  import { navigate } from "../../utils/index";

  const { isAuthorized } = getContext("isAuthorized");

  export let id;
  export let group;

  let form = {
    roles: [],
  };

  let autocompleteRoles = [];
  let roles = {};

  let groupRoles = [];
  let groupPolicies;

  let loading = {};
  let policiesTable;

  let userMissingRolePolicies;

  async function getGroupRoles() {
    loading.policies = true;
    try {
      const result = await GroupsRepository.getGroupRoles(id);

      groupRoles = result.list.map((r) => r);

      // if the group has a role with higher policies than the user
      if (roles.some((r) => groupRoles.includes(r.name) && !!r.missingPolicies))
        userMissingRolePolicies = true;

      form.roles = result.list.map((r) => (r = { label: r, value: r }));
    } finally {
      if (groupRoles.length) generatePoliciesFromRoles();
      loading.policies = false;
    }
  }

  function generatePoliciesFromRoles() {
    if (form.roles.length) {
      groupPolicies = roles
        .filter((r) => form.roles.some((fr) => fr.value === r.id))
        .flatMap((r) => r.policies);
    } else {
      groupPolicies = [];
    }
  }

  async function getRoles() {
    try {
      loading.roles = true;

      let rolesList = await RolesRepository.getRoles({ missingPolicies: true });
      rolesList = rolesList.list;

      const rolePolicies = await Promise.all(
        rolesList.map((r) => RolesRepository.getRolePolicies(r.id)),
      );
      roles = rolesList.map(
        (role, index) =>
          (role = { ...role, policies: rolePolicies[index].list }),
      );

      autocompleteRoles = rolesList.map(
        (role) =>
          (role = {
            label: role.name,
            value: role.id,
            disabled: !!role.missingPolicies,
          }),
      );
    } finally {
      loading.roles = false;
    }
  }

  async function handleSave() {
    const rolesToRemove =
      groupRoles.length > 0
        ? groupRoles.filter((g) => !form.roles.some((el) => el.value === g))
        : [];

    const rolesToAdd =
      groupRoles.length > 0
        ? form.roles.filter((g) => !groupRoles.some((el) => el === g.value))
        : form.roles;

    if (rolesToRemove.length === 0 && rolesToAdd.length === 0) {
      callout.set({
        msg: $_("no.changes.callout"),
        status: "warning",
        timeout: true,
      });
      navigate("/groups");
      return;
    }

    try {
      loading.submit = true;
      if (rolesToRemove.length > 0) {
        await GroupsRepository.deleteGroupRole(id, {
          roles: rolesToRemove.map((r) => r.toString()),
        });
      }

      if (rolesToAdd.length > 0) {
        await GroupsRepository.addGroupRole(id, {
          roles: rolesToAdd.map((r) => r.value.toString()),
        });
      }
      callout.set({
        msg: $_("policies.save.callout"),
        status: "success",
        timeout: true,
      });
    } finally {
      loading.submit = false;
      navigate("/groups");
    }
  }

  async function fetchAll() {
    await Promise.all([getRoles()]).then(() => {
      getGroupRoles().then(() => {
        generatePoliciesFromRoles();
      });
    });
  }

  $: if (form.roles) generatePoliciesFromRoles();

  onMount(() => {
    fetchAll();
  });
</script>

<svelte:head>
  <title>
    {$_("layout.group.roles")}: {group.id} | {$_("common.groups")} | {$_(
      "access-manager",
    )}
  </title>
</svelte:head>

<div class="my-3 mb-5">
  <CustomAutocomplete
    options={autocompleteRoles}
    bind:value={form.roles}
    readonly={!$isAuthorized("group_write") || userMissingRolePolicies}
  />
</div>

{#if groupPolicies}
  <PoliciesTable
    subPolicies={groupPolicies}
    disabled={true}
    bind:this={policiesTable}
  />
{/if}

{#if $isAuthorized("group_write") && !userMissingRolePolicies}
  <ActionFooter>
    <svelte:fragment slot="left">
      <Button
        on:click={() => {
          getGroupRoles();
          policiesTable.reset();
        }}
        outlined>{$_("common.reset")}</Button
      >
    </svelte:fragment>
    <Button on:click={handleSave} loading={loading.save}
      >{$_("buttons.save")}</Button
    >
    <Button on:click={() => navigate("/groups")} outlined
      >{$_("buttons.cancel")}</Button
    >
  </ActionFooter>
{/if}
